import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Ticket, ShieldCheck, Trophy, Plane, Snowflake, Car, ArrowRight, Church, Heart } from "lucide-react";
import { EthiopianCross } from "@/components/EthiopianCross";
import { Countdown } from "@/components/Countdown";
import { PurchaseForm } from "@/components/PurchaseForm";
import { api } from "@/lib/api";

const IMG = {
  hero: "https://images.unsplash.com/photo-1768383206344-dfeb4a00b18d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxldGhpb3BpYW4lMjBjaHVyY2glMjBidWlsZGluZ3xlbnwwfHx8fDE3ODI4NjQxMzF8MA&ixlib=rb-4.1.0&q=85",
  car: "/prize1-highlander.png",
  flight: "https://images.unsplash.com/photo-1717423689699-3f66fe6e29b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxhaXJwbGFuZSUyMGZseWluZyUyMG92ZXIlMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzgyODY0MTMxfDA&ixlib=rb-4.1.0&q=85",
  freezer: "/prize3-freezer.jpg",
};

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } };

const Header = () => (
  <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FAF9F6]/80 border-b border-[#E2D9C8]">
    <div className="max-w-7xl mx-auto px-6 md:px-12 min-h-16 py-2 flex items-center justify-between gap-3">
      <a href="#top" className="flex items-center gap-3" data-testid="brand-logo">
        <EthiopianCross className="w-11 h-11 shrink-0" />
        <div className="leading-tight max-w-[210px] sm:max-w-[420px]">
          <div className="font-heading font-bold text-[#9E1B1B] text-[13px] sm:text-base leading-[1.15]">Columbus Ohio Debre Genet St. Michael Cathedral</div>
          <div className="text-[8px] sm:text-[10px] tracking-[0.1em] uppercase text-[#4A4A4A] leading-[1.2]">and Abune Menfes Kidus Ethiopian Orthodox Tewahdo Church</div>
        </div>
      </a>
      <nav className="flex items-center gap-2 sm:gap-5 text-sm font-medium">
        <a href="#prizes" className="hidden sm:inline text-[#4A4A4A] hover:text-[#9E1B1B] transition-colors" data-testid="nav-prizes">Prizes</a>
        <a href="#how" className="hidden sm:inline text-[#4A4A4A] hover:text-[#9E1B1B] transition-colors" data-testid="nav-how">How it Works</a>
        <Link to="/lookup" className="text-[#4A4A4A] hover:text-[#9E1B1B] transition-colors flex items-center gap-1" data-testid="nav-lookup"><Search className="w-4 h-4" />Ticket Lookup</Link>
        <a href="#buy" className="bg-[#9E1B1B] hover:bg-[#7A1414] text-white px-4 py-2 rounded-md transition-colors" data-testid="nav-buy">Buy Tickets</a>
      </nav>
    </div>
  </header>
);

export default function Landing() {
  const [sold, setSold] = useState(null);

  useEffect(() => {
    api.get("/event/info").then(({ data }) => setSold(data.tickets_sold)).catch(() => {});
  }, []);

  return (
    <div id="top" className="min-h-screen bg-[#FAF9F6]">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#1A1A1A]">
        <div className="absolute inset-0">
          <img src="/raffle-flyer.jpg" alt="" aria-hidden="true" className="w-full h-full object-cover blur-2xl scale-110 opacity-30" />
          <div className="absolute inset-0 bg-[#1A1A1A]/70" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 md:px-10 py-12 md:py-16">
          <motion.div variants={fadeUp} initial="hidden" animate="show" className="flex justify-center">
            <img
              src="/raffle-flyer.jpg"
              alt="Columbus Ohio Debre Genet St. Michael Cathedral — Grand Fundraising Raffle flyer"
              className="w-full max-w-4xl rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.5)] border-2 border-[#C68E17]/50"
              data-testid="hero-flyer"
            />
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.15 }}
            className="mt-10 flex flex-col items-center gap-7">
            <div className="text-center">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C68E17] mb-3">Grand Drawing · January 24, 2027</p>
              <Countdown />
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a href="#buy" className="bg-[#9E1B1B] hover:bg-[#7A1414] text-white px-7 py-3.5 rounded-md font-semibold transition-colors flex items-center gap-2" data-testid="hero-buy-btn">
                <Ticket className="w-5 h-5" /> Get Your Tickets
              </a>
              <a href="#prizes" className="border-2 border-[#C68E17] text-[#C68E17] hover:bg-[#C68E17] hover:text-[#1A1A1A] px-7 py-3.5 rounded-md font-semibold transition-colors" data-testid="hero-prizes-btn">
                View Prizes
              </a>
            </div>
            {sold !== null && (
              <p className="text-sm text-white/70" data-testid="tickets-sold-count">
                <span className="font-mono font-bold text-[#C68E17]">{sold.toLocaleString()}</span> tickets sold so far — thank you!
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Prizes */}
      <section id="prizes" className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="max-w-2xl mb-12">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C68E17]">The Rewards</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-2">Three Extraordinary Prizes</h2>
          <p className="text-[#4A4A4A] mt-3">Winners are drawn at random from all paid tickets on the official drawing date. Each ticket can win only once.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="md:row-span-2 group bg-white border border-[#E2D9C8] rounded-2xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid="prize-1">
            <div className="relative h-64 md:h-[420px] overflow-hidden bg-white flex items-center justify-center">
              <img src={IMG.car} alt="Toyota Highlander" className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute top-4 left-4 bg-[#9E1B1B] text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1"><Trophy className="w-3.5 h-3.5" /> 1ST PRIZE</div>
            </div>
            <div className="p-8">
              <div className="flex items-center gap-2 text-[#C68E17] mb-2"><Car className="w-5 h-5" /><span className="text-xs uppercase tracking-widest font-semibold">Grand Prize</span></div>
              <h3 className="font-heading text-3xl font-bold">2026 Toyota Highlander</h3>
              <p className="text-[#4A4A4A] mt-2">A brand-new, three-row SUV — reliability and comfort for the whole family.</p>
              <div className="mt-5 font-mono text-2xl font-bold text-[#9E1B1B]">≈ $50,000 value</div>
            </div>
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} transition={{ delay: 0.1 }}
            className="group bg-white border border-[#E2D9C8] rounded-2xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid="prize-2">
            <div className="relative h-52 overflow-hidden">
              <img src={IMG.flight} alt="Flight to Ethiopia" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute top-4 left-4 bg-[#C68E17] text-white text-xs font-bold px-3 py-1.5 rounded-full">2ND PRIZE</div>
            </div>
            <div className="p-7">
              <div className="flex items-center gap-2 text-[#C68E17] mb-1"><Plane className="w-5 h-5" /></div>
              <h3 className="font-heading text-2xl font-bold">Round-trip Flight to Ethiopia</h3>
              <p className="text-[#4A4A4A] mt-1 text-sm">Reconnect with your roots — a journey home.</p>
              <div className="mt-3 font-mono text-xl font-bold text-[#9E1B1B]">≈ $2,000 value</div>
            </div>
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} transition={{ delay: 0.2 }}
            className="group bg-white border border-[#E2D9C8] rounded-2xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid="prize-3">
            <div className="relative h-52 overflow-hidden">
              <img src={IMG.freezer} alt="Deep Freezer" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute top-4 left-4 bg-[#4A4A4A] text-white text-xs font-bold px-3 py-1.5 rounded-full">3RD PRIZE</div>
            </div>
            <div className="p-7">
              <div className="flex items-center gap-2 text-[#C68E17] mb-1"><Snowflake className="w-5 h-5" /></div>
              <h3 className="font-heading text-2xl font-bold">Deep Freezer</h3>
              <p className="text-[#4A4A4A] mt-1 text-sm">A spacious chest freezer for your home.</p>
              <div className="mt-3 font-mono text-xl font-bold text-[#9E1B1B]">≈ $1,000 value</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-[#F3EFE6] border-y border-[#E2D9C8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mb-12">How It Works</h2>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { n: "01", t: "Fill the Form", d: "Enter your details and choose how many tickets you'd like." },
              { n: "02", t: "Pay Securely", d: "Checkout with credit or debit card via Stripe — fully PCI compliant." },
              { n: "03", t: "Get Your Numbers", d: "Receive your unique sequential ticket numbers instantly." },
              { n: "04", t: "Await the Drawing", d: "Winners are drawn transparently on January 24, 2027." },
            ].map((s) => (
              <div key={s.n} data-testid={`step-${s.n}`}>
                <div className="font-mono text-3xl font-bold text-[#C68E17]">{s.n}</div>
                <h3 className="font-heading text-xl font-semibold mt-2">{s.t}</h3>
                <p className="text-[#4A4A4A] text-sm mt-1">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Buy */}
      <section id="buy" className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-start">
        <div className="lg:sticky lg:top-24">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C68E17]">Buy Tickets</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-2">Reserve Your Entry</h2>
          <p className="text-[#4A4A4A] mt-3">Complete the form and pay securely. Your ticket numbers are generated automatically the moment your payment is confirmed.</p>
          <ul className="mt-6 space-y-3 text-sm text-[#4A4A4A]">
            <li className="flex items-center gap-3"><ShieldCheck className="w-5 h-5 text-[#2D6A4F]" /> Secure Stripe checkout — we never see your card details.</li>
            <li className="flex items-center gap-3"><Ticket className="w-5 h-5 text-[#2D6A4F]" /> Unique sequential ticket numbers.</li>
            <li className="flex items-center gap-3"><Search className="w-5 h-5 text-[#2D6A4F]" /> Verify any ticket anytime on our lookup page.</li>
          </ul>
        </div>
        <div className="bg-white border border-[#E2D9C8] rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-6 sm:p-10">
          <PurchaseForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-white/80">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 grid md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <EthiopianCross className="w-12 h-12 bg-white rounded-full p-1" />
              <div className="font-heading text-lg font-bold text-white">St. Michael Ethiopian Orthodox Church</div>
            </div>
            <p className="text-sm mt-4 text-white/60">Columbus, Ohio. A fundraising raffle in support of our church community.</p>
          </div>
          <div>
            <div className="font-semibold text-white mb-3">Quick Links</div>
            <div className="flex flex-col gap-2 text-sm">
              <a href="#prizes" className="hover:text-[#C68E17] transition-colors">Prizes</a>
              <a href="#buy" className="hover:text-[#C68E17] transition-colors">Buy Tickets</a>
              <Link to="/lookup" className="hover:text-[#C68E17] transition-colors">Ticket Lookup</Link>
              <Link to="/admin/login" className="hover:text-[#C68E17] transition-colors" data-testid="footer-admin">Admin</Link>
            </div>
          </div>
          <div>
            <div className="font-semibold text-white mb-3">Drawing Details</div>
            <p className="text-sm text-white/60">Grand drawing: <strong className="text-white">January 24, 2027</strong> <span className="text-[10px] text-red-500 font-medium" data-testid="drawing-date-disclaimer">*Date subject to change</span>. Winners drawn at random from all paid tickets. Need not be present to win.</p>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-white/40">
          © 2026 St. Michael Ethiopian Orthodox Church. This is a charitable fundraiser, not a gambling lottery.
        </div>
      </footer>
    </div>
  );
}
